import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, TextField, InputAdornment,
  Snackbar, Alert, Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import io from 'socket.io-client';
import voiceFlowConstraintService from '../../services/voiceFlowConstraintService';
import ConstraintTable from './ConstraintTable';
import ConstraintEditDialog from './ConstraintEditDialog';
import ConstraintDeleteDialog from './ConstraintDeleteDialog';

// CallLogPage 共用 StatItem
const StatItem = ({ label, value, color }) => (
  <Box sx={{ textAlign: 'center' }}>
    <Typography sx={{ fontSize: 24, fontWeight: 700, lineHeight: 1, color }}>{value}</Typography>
    <Typography sx={{ fontSize: 11, color: '#999', mt: '2px' }}>{label}</Typography>
  </Box>
);

// 「全部 / 啟用中」pill toggle（沿用 CallLog 來電/去電 pattern）
const FilterPill = ({ active, onClick, children }) => (
  <Box
    onClick={onClick}
    sx={{
      px: 1.5, py: 0.5, fontSize: 13, cursor: 'pointer', borderRadius: 1,
      bgcolor: active ? '#145da0' : '#f0f0f0',
      color: active ? '#fff' : '#444',
      fontWeight: active ? 600 : 400,
      transition: 'background-color 120ms',
    }}
  >
    {children}
  </Box>
);

// 算「30 天內生效」— start_date 在 [今天, 今天+30] 內
const isWithin30Days = (startDate) => {
  if (!startDate) return false;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setDate(end.getDate() + 30);
  const [y, m, d] = startDate.split('-').map(Number);
  const start = new Date(y, m - 1, d);
  return start >= now && start <= end;
};

const ConstraintTab = ({ flowId, flowName }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'enabled'
  const [editDialog, setEditDialog] = useState({ open: false, constraint: null, warnings: [] });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, constraint: null });
  const [notify, setNotify] = useState({ open: false, type: 'success', message: '' });
  const socketRef = useRef(null);

  const showSuccess = (message) => setNotify({ open: true, type: 'success', message });
  const showError = (message) => setNotify({ open: true, type: 'error', message });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const body = await voiceFlowConstraintService.list(flowId);
      setRows(body?.data || []);
    } catch (err) {
      showError(err.message);
    } finally {
      setLoading(false);
    }
  }, [flowId]);

  useEffect(() => { load(); }, [load]);

  // ============================================================
  // 🔥 Dedicated Socket.IO connection (mirrors FaqPage.js — known-good
  // pattern). The shared socketService had reliability issues for this
  // event; using a per-tab socket avoids the registration race.
  // ============================================================
  useEffect(() => {
    const socketUrl = process.env.REACT_APP_SOCKET_URL ||
      process.env.REACT_APP_API_URL?.replace('/api', '') ||
      window.location.origin;
    const token = localStorage.getItem('token');
    const companyId = (() => {
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        return u.companyId || u.company_id || null;
      } catch (_e) { return null; }
    })();

    let socket;
    try {
      socket = io(socketUrl, {
        auth: { token },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 2000,
      });

      socket.on('connect', () => {
        socket.emit('join_company', {
          companyId,
          userId: localStorage.getItem('userId') || 'anonymous'
        });
      });

      socket.on('reconnect', () => {
        socket.emit('join_company', {
          companyId,
          userId: localStorage.getItem('userId') || 'anonymous'
        });
      });

      const handleAudioStatus = (payload) => {
        if (!payload || !payload.constraintId) return;
        const targetId = String(payload.constraintId);
        const patch = {
          audioStatus: payload.audioStatus,
          audioAssetKey: payload.audioAssetKey,
          audioDuration: payload.audioDuration,
          audioPublicUrl: payload.audioPublicUrl,
          audioUpdatedAt: payload.audioUpdatedAt,
          hasAudio: payload.audioStatus === 'ready' ? true : undefined
        };
        const applyPatch = (row) => {
          const merged = { ...row };
          for (const k of Object.keys(patch)) {
            if (patch[k] !== undefined) merged[k] = patch[k];
          }
          return merged;
        };

        setRows((prev) => prev.map((r) =>
          String(r.id) !== targetId ? r : applyPatch(r)
        ));

        // Single source of truth: also patch the dialog's constraint prop
        // so an open dialog reflects the live update without its own
        // subscription.
        setEditDialog((prev) => {
          if (!prev.open || !prev.constraint) return prev;
          if (String(prev.constraint.id) !== targetId) return prev;
          return { ...prev, constraint: applyPatch(prev.constraint) };
        });
      };

      socket.on('constraint_audio_status', handleAudioStatus);
      socketRef.current = socket;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[ConstraintTab] socket init error', e);
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  const stats = useMemo(() => ({
    enabled: rows.filter((r) => r.enabled).length,
    disabled: rows.filter((r) => !r.enabled).length,
    upcoming: rows.filter((r) => r.enabled && isWithin30Days(r.startDate)).length,
  }), [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (activeFilter === 'enabled') list = list.filter((r) => r.enabled);
    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter((r) =>
        (r.name || '').toLowerCase().includes(s) ||
        (r.messageText || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [rows, search, activeFilter]);

  const handleCreateClick = () => {
    setEditDialog({ open: true, constraint: null, warnings: [] });
  };

  const handleEditClick = (row) => {
    setEditDialog({ open: true, constraint: row, warnings: [] });
  };

  const handleSubmit = async (form) => {
    setBusy(true);
    try {
      const body = editDialog.constraint
        ? await voiceFlowConstraintService.update(flowId, editDialog.constraint.id, form)
        : await voiceFlowConstraintService.create(flowId, form);
      const warnings = body?.warnings || [];
      if (warnings.length > 0) {
        setEditDialog((prev) => ({ ...prev, warnings, constraint: body.data }));
        showSuccess(editDialog.constraint ? '已更新' : '已新增（含提醒）');
      } else {
        setEditDialog({ open: false, constraint: null, warnings: [] });
        showSuccess(editDialog.constraint ? '已更新' : '已新增');
      }
      await load();
    } catch (err) {
      showError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const handleToggle = async (row) => {
    setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, enabled: !r.enabled } : r));
    try {
      await voiceFlowConstraintService.toggle(flowId, row.id);
    } catch (err) {
      showError(err.message);
      setRows((prev) => prev.map((r) => r.id === row.id ? { ...r, enabled: row.enabled } : r));
    }
  };

  const handleDelete = async () => {
    const target = deleteDialog.constraint;
    if (!target) return;
    setBusy(true);
    try {
      await voiceFlowConstraintService.remove(flowId, target.id);
      setDeleteDialog({ open: false, constraint: null });
      showSuccess(`已刪除「${target.name}」`);
      await load();
    } catch (err) {
      showError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* ── Header strip (CallLogPage pattern) ── */}
      <Box sx={{ flexShrink: 0, bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 3, pt: 2.5, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>日期/時間限制</Typography>
            <Typography sx={{ fontSize: 13, color: '#999' }}>
              {flowName || flowId} — 設定不接受訂位的日期或時段（如店休、節日 blackout）
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-end' }}>
            <StatItem label="啟用中"  value={stats.enabled}  color="#333" />
            <StatItem label="已停用"  value={stats.disabled} color="#999" />
            <StatItem label="30天內生效" value={stats.upcoming} color="#e65100" />
          </Box>
        </Box>
      </Box>

      {/* ── Search / filter row ── */}
      <Box sx={{
        flexShrink: 0, display: 'flex', alignItems: 'center', gap: 1.5,
        px: 3, py: 1.5, bgcolor: '#f9f9f9', borderBottom: '1px solid #f0f0f0',
      }}>
        <TextField
          size="small"
          placeholder="搜尋名稱或訊息..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
              </InputAdornment>
            ),
          }}
          sx={{ width: 220, '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'white' } }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FilterPill active={activeFilter === 'all'}     onClick={() => setActiveFilter('all')}>全部</FilterPill>
          <FilterPill active={activeFilter === 'enabled'} onClick={() => setActiveFilter('enabled')}>啟用中</FilterPill>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateClick}
          sx={{
            ml: 'auto', textTransform: 'none', bgcolor: '#145da0',
            '&:hover': { bgcolor: '#0f4a82' },
          }}
        >
          新增限制
        </Button>
      </Box>

      {/* ── Table ── */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 3, pb: 2 }}>
        <ConstraintTable
          rows={filteredRows}
          loading={loading}
          onEdit={handleEditClick}
          onDelete={(row) => setDeleteDialog({ open: true, constraint: row })}
          onToggle={handleToggle}
          busy={busy}
        />
      </Box>

      <ConstraintEditDialog
        open={editDialog.open}
        flowId={flowId}
        constraint={editDialog.constraint}
        warnings={editDialog.warnings}
        onCancel={() => setEditDialog({ open: false, constraint: null, warnings: [] })}
        onSubmit={handleSubmit}
        // Dialog calls this when 重新生成語音 auto-saves before enqueueing —
        // patches the table row + the dialog's constraint baseline so the
        // dirty check resets without reopening the dialog.
        onRowSaved={(updated) => {
          setRows((prev) => prev.map((r) => r.id === updated.id ? updated : r));
          setEditDialog((prev) => prev.open ? { ...prev, constraint: updated } : prev);
        }}
        saving={busy}
      />

      <ConstraintDeleteDialog
        open={deleteDialog.open}
        constraint={deleteDialog.constraint}
        onCancel={() => setDeleteDialog({ open: false, constraint: null })}
        onConfirm={handleDelete}
        loading={busy}
      />

      <Snackbar
        open={notify.open}
        autoHideDuration={3500}
        onClose={() => setNotify((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={notify.type}
          onClose={() => setNotify((prev) => ({ ...prev, open: false }))}
        >
          {notify.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConstraintTab;
